import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

interface ChargeModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function ChargeModal({ onClose, onSuccess }: ChargeModalProps) {
  const { user } = useAuth();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const presetAmounts = [10000, 30000, 50000, 100000, 300000, 500000];

  const handleCharge = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    const chargeAmount = parseInt(amount);
    if (!chargeAmount || chargeAmount < 1000) {
      alert('최소 충전 금액은 1,000원입니다.');
      return;
    }

    if (chargeAmount > 1000000) {
      alert('최대 충전 금액은 1,000,000원입니다.');
      return;
    }

    setLoading(true);

    try {
      // 결제 처리 (실제로는 결제 게이트웨이 연동 필요)
      // 여기서는 시뮬레이션으로 처리
      
      // 달란트 포인트 업데이트
      const { data: currentPoints } = await supabase
        .from('talent_points')
        .select('balance, total_earned')
        .eq('user_id', user.id)
        .single();

      if (currentPoints) {
        await supabase
          .from('talent_points')
          .update({
            balance: currentPoints.balance + chargeAmount,
            total_earned: currentPoints.total_earned + chargeAmount,
          })
          .eq('user_id', user.id);
      }

      // 거래 내역 생성
      await supabase
        .from('point_transactions')
        .insert({
          user_id: user.id,
          amount: chargeAmount,
          type: 'earn',
          description: '달란트 충전',
          reference_type: 'charge',
        });

      alert(`${chargeAmount.toLocaleString()}원이 충전되었습니다!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('충전 실패:', error);
      alert('충전 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">달란트 충전</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              충전 금액 선택
            </label>
            <div className="grid grid-cols-3 gap-3">
              {presetAmounts.map((preset) => (
                <button
                  key={preset}
                  onClick={() => setAmount(preset.toString())}
                  className={`py-3 px-4 rounded-lg border-2 font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    amount === preset.toString()
                      ? 'border-teal-600 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {(preset / 10000).toFixed(0)}만원
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              직접 입력
            </label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="충전할 금액을 입력하세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                min="1000"
                max="1000000"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                원
              </span>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              최소 1,000원 ~ 최대 1,000,000원
            </p>
          </div>

          {amount && parseInt(amount) >= 1000 && (
            <div className="bg-teal-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-teal-800 font-medium">충전 후 잔액</span>
                <span className="text-lg font-bold text-teal-600">
                  <i className="ri-coin-line mr-1"></i>
                  {parseInt(amount).toLocaleString()} 달란트
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              취소
            </button>
            <button
              onClick={handleCharge}
              disabled={loading || !amount || parseInt(amount) < 1000}
              className="flex-1 py-3 px-6 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {loading ? '처리 중...' : '충전하기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
